/**
 * WabaProfileView — WhatsApp Business profile detail panel.
 *
 * Shown in the conversation sidebar when the user clicks the profile icon
 * next to the WABA sender dropdown. Displays business profile info fetched
 * from the WhatsApp Business Profile API (/{phone_number_id}/whatsapp_business_profile).
 *
 * Supports inline editing of editable fields (about, description, address,
 * email, websites, vertical). Calls the update API on save.
 */
import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  Building2,
  Globe,
  Mail,
  MapPin,
  Phone,
  Info,
  Loader2,
  ShieldCheck,
  BadgeCheck,
  Pencil,
  Save,
  X,
  Plus,
  Trash2,
  Camera,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { useWhatsAppStore } from '@/stores/whatsapp-store';
import { WhatsAppAPI } from '@/modules/tpa/whatsapp/whatsapp-api';
import type { Sender } from '@/types/messaging';

interface WabaProfileViewProps {
  sender: Sender;
  onBack: () => void;
}

interface BusinessProfile {
  about?: string;
  address?: string;
  description?: string;
  email?: string;
  profilePictureUrl?: string;
  websites?: string[];
  vertical?: string;
}

/** WhatsApp Business Profile vertical (industry) options */
const VERTICAL_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Not set' },
  { value: 'AUTO', label: 'Automotive' },
  { value: 'BEAUTY', label: 'Beauty' },
  { value: 'APPAREL', label: 'Apparel' },
  { value: 'EDU', label: 'Education' },
  { value: 'ENTERTAIN', label: 'Entertainment' },
  { value: 'EVENT_PLAN', label: 'Event Planning' },
  { value: 'FINANCE', label: 'Finance' },
  { value: 'GROCERY', label: 'Grocery' },
  { value: 'GOVT', label: 'Government' },
  { value: 'HOTEL', label: 'Hotel' },
  { value: 'HEALTH', label: 'Health' },
  { value: 'NONPROFIT', label: 'Non-Profit' },
  { value: 'PROF_SERVICES', label: 'Professional Services' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'RESTAURANT', label: 'Restaurant' },
  { value: 'OTHER', label: 'Other' },
];

/** localStorage key for caching a WABA sender's profile picture URL */
const PROFILE_PIC_CACHE_KEY = 'waba_profile_pic_';

/** Read cached profile picture URL from localStorage */
export function getCachedProfilePic(senderId: string): string | null {
  try {
    return localStorage.getItem(`${PROFILE_PIC_CACHE_KEY}${senderId}`) || null;
  } catch {
    return null;
  }
}

/** Write profile picture URL to localStorage cache */
function setCachedProfilePic(senderId: string, url: string | undefined) {
  try {
    if (url) {
      localStorage.setItem(`${PROFILE_PIC_CACHE_KEY}${senderId}`, url);
    }
  } catch {
    // localStorage not available
  }
}

export function WabaProfileView({ sender, onBack }: WabaProfileViewProps) {
  const extensionName = useAppStore((s) => s.extensionName);
  // Shared/team users (anyone viewing data via `selectedAdminUserId`) get a
  // read-only profile — they shouldn't be able to mutate the owner's WhatsApp
  // Business profile, change the avatar, or edit fields.
  const isSharedUser = useAuthStore((s) => !!s.selectedAdminUserId);
  const canEdit = !isSharedUser;
  const [profile, setProfile] = useState<BusinessProfile | null>(() => {
    // Pre-populate with cached profile picture for instant display
    const cachedPic = getCachedProfilePic(sender.id);
    return cachedPic ? { profilePictureUrl: cachedPic } : null;
  });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const apiRef = useRef<WhatsAppAPI | null>(null);

  // Draft fields for edit mode
  const [draftAbout, setDraftAbout] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftAddress, setDraftAddress] = useState('');
  const [draftEmail, setDraftEmail] = useState('');
  const [draftVertical, setDraftVertical] = useState('');
  const [draftWebsites, setDraftWebsites] = useState<string[]>([]);
  const [draftProfilePicUrl, setDraftProfilePicUrl] = useState('');
  const profilePicInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!extensionName || !sender.id) return;

    if (!apiRef.current) {
      apiRef.current = new WhatsAppAPI(extensionName);
    }

    let cancelled = false;
    setLoading(true);

    apiRef.current.getBusinessProfile(sender.id).then((resp) => {
      if (cancelled) return;
      if (resp.success && resp.data?.data?.[0]) {
        const d = resp.data.data[0];
        const picUrl = d.profile_picture_url;
        // Cache the profile picture URL for instant loading next time
        setCachedProfilePic(sender.id, picUrl);
        setProfile({
          about: d.about,
          address: d.address,
          description: d.description,
          email: d.email,
          profilePictureUrl: picUrl,
          websites: d.websites,
          vertical: d.vertical,
        });
      } else {
        setProfile({});
      }
      setLoading(false);
    }).catch(() => {
      if (!cancelled) {
        setProfile((prev) => prev || {});
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [extensionName, sender.id]);

  const verifiedName = (sender.data?.verifiedName as string) || sender.label;
  const phoneNumber = sender.phoneNumber || '';
  // Prefer wabaId stamped on the sender; fall back to the store's
  // number→WABA map (populated at sender-load time via registerSender) so
  // the "Manage on Facebook" link still works for senders that didn't carry
  // wabaId in their data payload.
  const msisdnFromSender = ((sender.data?.msisdn as string) || sender.phoneNumber || '').replace(/\D/g, '');
  const wabaIdFromStore = useWhatsAppStore((s) => msisdnFromSender ? s.numbersVsWabaIdMap[msisdnFromSender] : undefined);
  const wabaId = (sender.data?.wabaId as string) || wabaIdFromStore || '';
  const initials = verifiedName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleStartEdit = () => {
    setDraftAbout(profile?.about || '');
    setDraftDescription(profile?.description || '');
    setDraftAddress(profile?.address || '');
    setDraftEmail(profile?.email || '');
    setDraftVertical(profile?.vertical || '');
    setDraftWebsites(profile?.websites?.length ? [...profile.websites] : []);
    setDraftProfilePicUrl(profile?.profilePictureUrl || '');
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
  };

  const handleSave = async () => {
    if (!apiRef.current) return;
    setSaving(true);

    try {
      const resp = await apiRef.current.updateBusinessProfile(sender.id, {
        messaging_product: 'whatsapp',
        about: draftAbout || undefined,
        description: draftDescription || undefined,
        address: draftAddress || undefined,
        email: draftEmail || undefined,
        vertical: draftVertical || undefined,
        websites: draftWebsites.filter((w) => w.trim()) || undefined,
        profile_picture_url: draftProfilePicUrl || undefined,
      });

      if (resp.success) {
        // Update local profile state
        setProfile((prev) => ({
          ...prev,
          about: draftAbout || undefined,
          description: draftDescription || undefined,
          address: draftAddress || undefined,
          email: draftEmail || undefined,
          vertical: draftVertical || undefined,
          websites: draftWebsites.filter((w) => w.trim()),
          profilePictureUrl: draftProfilePicUrl || prev?.profilePictureUrl,
        }));
        setEditing(false);
        toast.success('Profile updated successfully');
      } else {
        const errMsg = (resp as Record<string, unknown>).errorDescription || 'Failed to update profile';
        toast.error('Update failed', { description: String(errMsg) });
      }
    } catch {
      toast.error('Update failed', { description: 'An unexpected error occurred' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddWebsite = () => {
    if (draftWebsites.length < 2) {
      setDraftWebsites([...draftWebsites, '']);
    }
  };

  const handleRemoveWebsite = (index: number) => {
    setDraftWebsites(draftWebsites.filter((_, i) => i !== index));
  };

  const handleWebsiteChange = (index: number, value: string) => {
    const updated = [...draftWebsites];
    updated[index] = value;
    setDraftWebsites(updated);
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-card">
      {/* Header */}
      <div className="relative flex flex-col items-center px-4 pt-4 pb-6 bg-gradient-to-b from-[#00a884]/10 to-transparent dark:from-[#00a884]/5">
        {/* Back button */}
        <button
          onClick={onBack}
          className="absolute top-3 left-3 rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>

        {/* Edit / Save / Cancel buttons — owner only */}
        {!loading && !editing && canEdit && (
          <button
            onClick={handleStartEdit}
            className="absolute top-3 right-3 rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            title="Edit profile"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}

        {/* Profile picture */}
        <div className="mt-4 mb-3 relative group">
          {loading ? (
            <div className="h-20 w-20 rounded-full bg-muted animate-pulse" />
          ) : (
            <>
              {(editing ? draftProfilePicUrl : profile?.profilePictureUrl) ? (
                <img
                  src={editing ? draftProfilePicUrl : profile?.profilePictureUrl}
                  alt={verifiedName}
                  className="h-20 w-20 rounded-full object-cover border-2 border-white dark:border-card shadow-md"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className={cn(
                  'h-20 w-20 rounded-full bg-[#00a884] items-center justify-center text-white text-2xl font-semibold shadow-md',
                  (editing ? draftProfilePicUrl : profile?.profilePictureUrl) ? 'hidden' : 'flex'
                )}
              >
                {initials || '?'}
              </div>
              {/* Camera overlay — always visible in edit mode, hover in view mode */}
              {editing && (
                <button
                  type="button"
                  onClick={() => profilePicInputRef.current?.click()}
                  className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-white transition-opacity"
                  title="Change profile picture"
                >
                  <Camera className="h-5 w-5" />
                </button>
              )}
              {!editing && !loading && canEdit && (
                <button
                  type="button"
                  onClick={handleStartEdit}
                  className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Edit profile picture"
                >
                  <Camera className="h-5 w-5" />
                </button>
              )}
              {/* Hidden file input for profile pic URL (WhatsApp API needs a public URL, not a file upload) */}
              {editing && (
                <input
                  ref={profilePicInputRef}
                  type="text"
                  className="hidden"
                />
              )}
            </>
          )}
        </div>

        {/* Profile picture URL input (shown in edit mode) */}
        {editing && (
          <div className="w-full px-4 mb-2">
            <div className="flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1.5 focus-within:ring-1 focus-within:ring-primary/40">
              <Camera className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <input
                value={draftProfilePicUrl}
                onChange={(e) => setDraftProfilePicUrl(e.target.value)}
                placeholder="Profile picture URL (https://...)"
                className="flex-1 bg-transparent text-xs outline-none text-foreground placeholder:text-muted-foreground/50"
              />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1 px-0.5">Enter a public image URL for the profile picture</p>
          </div>
        )}

        {/* Name with verified badge */}
        <div className="flex items-center gap-1.5 text-center">
          <h3 className="text-lg font-semibold text-foreground leading-tight">
            {verifiedName}
          </h3>
          <BadgeCheck className="h-4 w-4 text-[#00a884] shrink-0" />
        </div>

        {/* Phone number */}
        {phoneNumber && (
          <p className="mt-0.5 text-sm text-muted-foreground">{phoneNumber}</p>
        )}

        {/* Manage on Facebook — placed in the header so it's the first action
            owners reach for. Always shown when wabaId is known; Facebook
            itself enforces who actually has access on the other side. */}
        {wabaId && (
          <a
            href={`https://business.facebook.com/latest/whatsapp_manager/phone_numbers?asset_id=${encodeURIComponent(wabaId)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#1877F2]/30 bg-[#1877F2]/5 px-3 py-1 text-[11px] font-medium text-[#1877F2] hover:bg-[#1877F2]/10 hover:border-[#1877F2]/50 transition-colors"
          >
            <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded bg-[#1877F2] text-white text-[8px] font-bold">f</span>
            Manage on Facebook
            <ExternalLink className="h-3 w-3 opacity-70" />
          </a>
        )}
      </div>

      {/* Details */}
      <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-3 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : editing ? (
          /* ── Edit Mode ── */
          <>
            {/* About */}
            <EditField
              icon={<Info className="h-4 w-4" />}
              label="About"
              value={draftAbout}
              onChange={setDraftAbout}
              placeholder="Hey there! I am using WhatsApp."
              maxLength={139}
            />

            {/* Description */}
            <EditField
              icon={<Info className="h-4 w-4" />}
              label="Description"
              value={draftDescription}
              onChange={setDraftDescription}
              placeholder="Tell customers about your business"
              multiline
              maxLength={512}
            />

            {/* Category / Vertical */}
            <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-muted-foreground"><Building2 className="h-4 w-4" /></span>
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  Category
                </span>
              </div>
              <div className="pl-6">
                <select
                  value={draftVertical}
                  onChange={(e) => setDraftVertical(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {VERTICAL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Address */}
            <EditField
              icon={<MapPin className="h-4 w-4" />}
              label="Address"
              value={draftAddress}
              onChange={setDraftAddress}
              placeholder="Business address"
              multiline
              maxLength={256}
            />

            {/* Email */}
            <EditField
              icon={<Mail className="h-4 w-4" />}
              label="Email"
              value={draftEmail}
              onChange={setDraftEmail}
              placeholder="business@example.com"
              maxLength={128}
            />

            {/* Websites */}
            <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-muted-foreground"><Globe className="h-4 w-4" /></span>
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  Websites
                </span>
                <span className="text-[10px] text-muted-foreground/60 ml-auto">max 2</span>
              </div>
              <div className="pl-6 space-y-2">
                {draftWebsites.map((url, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => handleWebsiteChange(i, e.target.value)}
                      placeholder="https://example.com"
                      maxLength={256}
                      className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                    <button
                      onClick={() => handleRemoveWebsite(i)}
                      className="shrink-0 rounded p-1 text-muted-foreground hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {draftWebsites.length < 2 && (
                  <button
                    onClick={handleAddWebsite}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                  >
                    <Plus className="h-3 w-3" />
                    Add website
                  </button>
                )}
              </div>
            </div>

            {/* Save / Cancel */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1"
                size="sm"
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : (
                  <Save className="h-3.5 w-3.5 mr-1.5" />
                )}
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outline"
                onClick={handleCancelEdit}
                disabled={saving}
                size="sm"
              >
                <X className="h-3.5 w-3.5 mr-1.5" />
                Cancel
              </Button>
            </div>
          </>
        ) : (
          /* ── View Mode ── */
          <>
            {/* About */}
            {profile?.about && (
              <ProfileSection
                icon={<Info className="h-4 w-4" />}
                label="About"
                value={profile.about}
              />
            )}

            {/* Description */}
            {profile?.description && (
              <ProfileSection
                icon={<Info className="h-4 w-4" />}
                label="Description"
                value={profile.description}
              />
            )}

            {/* Business vertical */}
            {profile?.vertical && (
              <ProfileSection
                icon={<Building2 className="h-4 w-4" />}
                label="Category"
                value={formatVertical(profile.vertical)}
              />
            )}

            {/* Address */}
            {profile?.address && (
              <ProfileSection
                icon={<MapPin className="h-4 w-4" />}
                label="Address"
                value={profile.address}
              />
            )}

            {/* Email */}
            {profile?.email && (
              <ProfileSection
                icon={<Mail className="h-4 w-4" />}
                label="Email"
                value={profile.email}
              />
            )}

            {/* Websites */}
            {profile?.websites && profile.websites.length > 0 && (
              <ProfileSection
                icon={<Globe className="h-4 w-4" />}
                label="Websites"
              >
                <div className="space-y-1">
                  {profile.websites.map((url, i) => (
                    <a
                      key={i}
                      href={url.startsWith('http') ? url : `https://${url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-sm text-primary hover:underline truncate"
                    >
                      {url}
                    </a>
                  ))}
                </div>
              </ProfileSection>
            )}

            {/* Phone number */}
            {phoneNumber && (
              <ProfileSection
                icon={<Phone className="h-4 w-4" />}
                label="Phone"
                value={phoneNumber}
              />
            )}

            {/* WABA ID */}
            {wabaId && (
              <ProfileSection
                icon={<ShieldCheck className="h-4 w-4" />}
                label="WABA ID"
                value={wabaId}
              />
            )}

            {/* Empty state when no profile info at all */}
            {!profile?.about &&
              !profile?.description &&
              !profile?.vertical &&
              !profile?.address &&
              !profile?.email &&
              (!profile?.websites || profile.websites.length === 0) && (
              <div className="text-center py-6">
                <p className="text-sm text-muted-foreground">
                  No additional profile information available.
                </p>
                {canEdit && (
                  <button
                    onClick={handleStartEdit}
                    className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Add profile info
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Single profile info row (view mode) */
function ProfileSection({
  icon,
  label,
  value,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-muted-foreground">{icon}</span>
        <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
          {label}
        </span>
      </div>
      {value ? (
        <p className="text-sm text-foreground whitespace-pre-wrap pl-6">{value}</p>
      ) : (
        <div className="pl-6">{children}</div>
      )}
    </div>
  );
}

/** Single editable field (edit mode) */
function EditField({
  icon,
  label,
  value,
  onChange,
  placeholder,
  multiline,
  maxLength,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  maxLength?: number;
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-muted-foreground">{icon}</span>
        <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
          {label}
        </span>
        {maxLength && (
          <span className="text-[10px] text-muted-foreground/60 ml-auto">
            {value.length}/{maxLength}
          </span>
        )}
      </div>
      <div className="pl-6">
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            rows={3}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-ring resize-none"
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          />
        )}
      </div>
    </div>
  );
}

/** Format business vertical slug to readable text */
function formatVertical(vertical: string): string {
  const match = VERTICAL_OPTIONS.find((v) => v.value === vertical);
  if (match) return match.label;
  return vertical
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
